// Drawola Studio: keeps the hidden editor out of reach while the Studio shows.
//
// Fork-only. No upstream counterpart, so it never conflicts on merge.
//
// The editor stays mounted behind the Studio, and studio.scss only hides it
// with `visibility`, which upstream buttons override (ToolIcon.scss sets
// `visibility: visible` on some), so Tab still reached them. Native `inert`
// takes the whole subtree out of focus and pointer reach. It does not touch
// the editor's `handleKeyboardGlobally` listener on `document`, which also
// gets keys aimed at <body>, so a keydown capture guard on `window` stops
// those. Keyup is left alone on purpose: a key held in the editor (Space for
// pan) is often released after Home moved focus to <body>, and the editor
// must see that release or it keeps the hold when the draw opens again.

import { useLayoutEffect } from "react";

/**
 * Makes `container` inert and keeps body and editor keydowns from reaching
 * `document` while `active`; restores both on deactivate or unmount.
 */
export const useStudioEditorIsolation = (
  container: HTMLElement | null,
  active: boolean,
): void => {
  useLayoutEffect(() => {
    if (!active || !container) {
      return;
    }
    const doc = container.ownerDocument;
    const win = doc.defaultView;
    const previousInert = container.getAttribute("inert");
    container.setAttribute("inert", "");

    // Only propagation stops: the browser default (Tab, its own shortcuts)
    // still runs, and Studio or portal targets are never touched.
    const guard = (event: Event) => {
      const { target } = event;
      if (
        target === doc.body ||
        target === doc.documentElement ||
        (target instanceof Node && container.contains(target))
      ) {
        event.stopPropagation();
      }
    };
    win?.addEventListener("keydown", guard, true);

    return () => {
      win?.removeEventListener("keydown", guard, true);
      if (previousInert === null) {
        container.removeAttribute("inert");
      } else {
        container.setAttribute("inert", previousInert);
      }
    };
  }, [container, active]);
};
